import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import { FolderCog, PlusIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import ActivityService from "services/Activity";

function CategoryRow({ category, onSuccess }) {
  const [name, setName] = useState(category.text);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const renameMutation = useMutation({
    mutationFn: name => ActivityService.editCategory(category.value, { name }),
    onSuccess,
    onError: () => {
      toast.error("Failed to rename category");
      setName(category.text);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => ActivityService.deleteCategory(category.value),
    onSuccess: () => {
      onSuccess();
      setDeleteOpen(false);
    },
    onError: () => toast.error("Failed to delete category"),
  });

  const handleBlur = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === category.text) {
      setName(category.text);
      return;
    }
    renameMutation.mutate(trimmed);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      <div className="tw:flex-1">
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={renameMutation.isPending}
        />
      </div>
      <Button
        type="button"
        variant="destructive"
        size="icon-sm"
        aria-label="Delete category"
        onClick={() => setDeleteOpen(true)}
      >
        <Trash2 />
      </Button>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete category?"
        description={`Are you sure you want to delete the category "${category.text}"?`}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}

function NewCategoryRow({ onSuccess }) {
  const [name, setName] = useState("");

  const createMutation = useMutation({
    mutationFn: () => ActivityService.createCategory(name.trim()),
    onSuccess: () => {
      setName("");
      onSuccess();
    },
    onError: () => toast.error("Failed to create category"),
  });

  const handleSubmit = () => {
    if (!name.trim()) return;
    createMutation.mutate();
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      <div className="tw:flex-1">
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="New category name"
          disabled={createMutation.isPending}
        />
      </div>
      <Button
        type="button"
        size="sm"
        disabled={!name.trim() || createMutation.isPending}
        onClick={handleSubmit}
      >
        Add
      </Button>
    </div>
  );
}

function ManageCategoriesModal({
  categories,
  fetchCategories,
  open,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);

  // When opened from the editor toolbar the modal is controlled and carries no
  // trigger of its own; left uncontrolled it triggers itself with the icon.
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;
  const setDialogOpen = isControlled ? onOpenChange : setInternalOpen;

  const handleSuccess = useCallback(async () => {
    await fetchCategories();
  }, [fetchCategories]);

  const handleNewSuccess = useCallback(async () => {
    await fetchCategories();
    setShowNewRow(false);
  }, [fetchCategories]);

  const visibleCategories = categories.filter(c => !c.hidden);

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!isControlled && (
        <DialogTrigger
          render={
            <Button type="button" variant="outline" size="icon-sm">
              <FolderCog className="tw:size-4" />
            </Button>
          }
        />
      )}
      <DialogContent>
        <DialogTitle>Manage Categories</DialogTitle>
        <div className="tw:flex tw:flex-col tw:gap-2">
          {visibleCategories.map(category => (
            <CategoryRow
              key={category.value}
              category={category}
              onSuccess={handleSuccess}
            />
          ))}
          {visibleCategories.length === 0 && (
            <p className="tw:text-sm tw:text-muted-foreground tw:text-center tw:py-4">
              No categories
            </p>
          )}
          {showNewRow && <NewCategoryRow onSuccess={handleNewSuccess} />}
          <div className="tw:flex tw:justify-center tw:mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowNewRow(true)}
              disabled={showNewRow}
            >
              <PlusIcon data-icon="inline-start" />
              New Category
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

ManageCategoriesModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchCategories: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export default ManageCategoriesModal;
